<template>
  <div class="calendar-pc">
    <div class="title">
      <div class="left center">
        <i class="fas fa-calendar-days title-icon" />
        <span class="text">現在の{{ rangeMode ? '期間' : '日付' }}：{{ titleDateText }}</span>
      </div>
      <button type="button" class="right center" @click="handleBack">
        <span class="text">戻る</span>
        <i class="fas fa-chevron-right back-icon" />
      </button>
    </div>

    <div class="content">
      <!-- 単日／期間の切替。期間は最長3か月（開始日 + 3か月まで） -->
      <div class="mode-bar">
        <div class="mode-switch">
          <button
            type="button"
            class="mode-switch__btn"
            :class="{ active: !rangeMode }"
            @click="setRangeMode(false)"
          >
            1日
          </button>
          <button
            type="button"
            class="mode-switch__btn"
            :class="{ active: rangeMode }"
            @click="setRangeMode(true)"
          >
            期間
          </button>
        </div>

        <template v-if="rangeMode">
          <div class="range-fields">
            <button
              type="button"
              class="range-field"
              :class="{ active: activeField === 'start' }"
              @click="activeField = 'start'"
            >
              <span class="range-field__label">開始日</span>
              <span class="range-field__value">{{ rangeStartText }}</span>
            </button>
            <span class="range-sep">〜</span>
            <button
              type="button"
              class="range-field"
              :class="{ active: activeField === 'end' }"
              @click="activeField = 'end'"
            >
              <span class="range-field__label">終了日</span>
              <span class="range-field__value">{{ rangeEndText }}</span>
            </button>
          </div>

          <div class="range-actions">
            <span class="range-hint">最長{{ MAX_RANGE_MONTHS }}か月</span>
            <button type="button" class="range-apply" :disabled="!rangeValid" @click="applyRange">
              適用
            </button>
          </div>
        </template>
      </div>

      <!-- 不正な期間はここで止める（サーバーへは投げない） -->
      <p v-if="rangeMode && rangeError" class="range-error" role="alert">
        <i class="fas fa-circle-exclamation" aria-hidden="true" />
        {{ rangeError }}
      </p>

      <div class="header">
        <div class="years">
          <button type="button" class="y-current center" @click="toggleYearDropMenu">
            {{ viewYear }}
            <span class="caret" :class="{ up: showYearDropMenu }" />
          </button>
          <div v-if="showYearDropMenu" ref="yearDropdown" class="y-dropdown">
            <button
              v-for="year in yearRange"
              :key="year"
              type="button"
              class="y-menu center"
              :class="{ select: year === viewYear }"
              @click="onYearClick(year)"
            >
              {{ year }}
            </button>
          </div>
        </div>

        <div class="months">
          <button
            v-for="(label, idx) in monthLabels"
            :key="label"
            type="button"
            class="month center"
            :class="{ select: idx + 1 === viewMonth }"
            @click="onMonthClick(idx)"
          >
            {{ label }}
          </button>
        </div>
      </div>

      <div class="body">
        <div class="weeks">
          <div v-for="week in weekLabels" :key="week" class="week center">{{ week }}</div>
        </div>
        <div class="dates">
          <button
            v-for="(cell, idx) in monthDates"
            :key="idx"
            type="button"
            class="date"
            :class="{
              disable: !cell.currentMonth,
              select: isSelected(cell),
              'range-edge': isRangeEdge(cell),
              'in-range': isInRange(cell),
              today: isTodayCell(cell),
            }"
            :disabled="!cell.currentMonth"
            @click="onDateClick(idx)"
          >
            <div class="num">{{ cell.date }}</div>
            <div v-if="isTodayCell(cell)" class="today-label">今日</div>
            <div
              v-if="cell.festival"
              class="festival"
              :class="{ 'festival--long': cell.festival.length > 6 }"
            >
              {{ cell.festival }}
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import dayjs from 'dayjs'
import { getJapaneseHolidayLabel } from '@/utils/japanese-holidays'
import { MAX_RANGE_MONTHS, formatRangeLabel, isRangeMode, validateRange } from '@/utils/dashboard-date-range'

const props = defineProps({
  /** 選択中の開始日（'YYYY-M-D'）。単日モードでは終了日と同じ値 */
  selectStartDate: {
    type: String,
    default: '',
  },
  /** 選択中の終了日（'YYYY-M-D'） */
  selectEndDate: {
    type: String,
    default: '',
  },
  selectMinDate: {
    type: String,
    default: '2011-1-1',
  },
  selectMaxDate: {
    type: String,
    default: () => {
      const today = new Date()
      return `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`
    },
  },
})

const emit = defineEmits(['back'])

const monthLabels = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']
const weekLabels = ['月', '火', '水', '木', '金', '土', '日']

const showYearDropMenu = ref(false)
const yearDropdown = ref(null)
const pickedDate = ref([0, 0, 0])
/** 期間モードか。単日モードは日付クリックで即確定、期間モードは開始日・終了日を埋めて「適用」で確定 */
const rangeMode = ref(false)
/** 期間モードで次にクリックが入る側 */
const activeField = ref('start')
/** 期間モードの開始日・終了日（'YYYY-MM-DD'） */
const rangeStart = ref('')
const rangeEnd = ref('')
const viewMonth = ref(0)
const viewYear = ref(0)
const monthDates = ref([])

const titleDateText = computed(() => {
  if (rangeMode.value) {
    if (!rangeStart.value || !rangeEnd.value) return ''
    return formatRangeLabel(rangeStart.value, rangeEnd.value)
  }
  const [y, m, d] = pickedDate.value
  return y ? `${y}.${m}.${d}` : ''
})

const rangeStartText = computed(() => (rangeStart.value ? dayjs(rangeStart.value).format('YYYY/M/D') : '未選択'))
const rangeEndText = computed(() => (rangeEnd.value ? dayjs(rangeEnd.value).format('YYYY/M/D') : '未選択'))

/** 期間の妥当性（終了日 < 開始日 / 3か月超）。バックエンドの DpDateRange と同じ判定 */
const rangeCheck = computed(() => {
  if (!rangeStart.value || !rangeEnd.value) {
    return { valid: false, message: '' }
  }
  return validateRange(rangeStart.value, rangeEnd.value)
})
const rangeValid = computed(() => rangeCheck.value.valid)
const rangeError = computed(() => rangeCheck.value.message)

const yearRange = computed(() => {
  const currentYear = new Date().getFullYear()
  const minYear = currentYear - 50
  const maxYear = currentYear + 50
  const years = []
  for (let year = minYear; year <= maxYear; year += 1) {
    years.push(year)
  }
  return years
})

function dateToArray(dateStr) {
  if (!dateStr) return []
  return dateStr.split('-').map(Number)
}

function getMaxDateByYM(year, month) {
  return new Date(year, month, 0).getDate()
}

function resolveCellYearMonth(year, month, cell) {
  if (cell.lastMonth) {
    if (month === 1) return { year: year - 1, month: 12 }
    return { year, month: month - 1 }
  }
  if (cell.nextMonth) {
    if (month === 12) return { year: year + 1, month: 1 }
    return { year, month: month + 1 }
  }
  return { year, month }
}

function getFestivalLabel(year, month, day) {
  return getJapaneseHolidayLabel(year, month, day)
}

function attachFestival(year, month, cell) {
  const { year: cellYear, month: cellMonth } = resolveCellYearMonth(year, month, cell)
  return {
    ...cell,
    festival: getFestivalLabel(cellYear, cellMonth, cell.date),
  }
}

function getMonthBounds(year, month) {
  const maxDay = getMaxDateByYM(year, month)
  return { minDay: 1, maxDay }
}

function syncPickedDateToViewMonth() {
  const { minDay, maxDay } = getMonthBounds(viewYear.value, viewMonth.value)
  let day = pickedDate.value[2] || minDay
  day = Math.min(Math.max(day, minDay), maxDay)
  pickedDate.value = [viewYear.value, viewMonth.value, day]
}

function getDatesByYM(year, month) {
  const firstDay = new Date(year, month - 1, 1).getDay()
  const lastDay = new Date(year, month, 0).getDay()
  const lastMonthDates = []

  if (firstDay !== 1) {
    const lastMonthMaxDate = getMaxDateByYM(year, month - 1)
    const lastDaysCount = firstDay === 0 ? 6 : firstDay - 1
    for (let i = 0; i < lastDaysCount; i += 1) {
      const cur = firstDay === 0 ? lastMonthMaxDate - 5 + i : lastMonthMaxDate - firstDay + 2 + i
      lastMonthDates.push(attachFestival(year, month, { date: cur, lastMonth: true }))
    }
  }

  const nextMonthDates = []
  if (lastDay > 0) {
    for (let i = 0; i < 7 - lastDay; i += 1) {
      nextMonthDates.push(attachFestival(year, month, { date: i + 1, nextMonth: true }))
    }
  }

  const currentMonthDates = []
  const maxDate = getMaxDateByYM(year, month)
  for (let i = 0; i < maxDate; i += 1) {
    const day = i + 1
    currentMonthDates.push(
      attachFestival(year, month, {
        date: day,
        currentMonth: true,
        disabled: false,
      }),
    )
  }

  return lastMonthDates.concat(currentMonthDates).concat(nextMonthDates)
}

function initCalendar() {
  const startStr = props.selectStartDate || props.selectEndDate || props.selectMaxDate
  const endStr = props.selectEndDate || props.selectStartDate || props.selectMaxDate
  // 親から期間が渡っていれば期間モードで開く（前回の選択状態をそのまま復元する）
  rangeMode.value = isRangeMode(dayjs(startStr), dayjs(endStr))
  rangeStart.value = dayjs(startStr).format('YYYY-MM-DD')
  rangeEnd.value = dayjs(endStr).format('YYYY-MM-DD')
  activeField.value = 'start'

  // カレンダーの表示月は「起点」に合わせる（期間モードでまず開始日を選ぶため）
  const [year, month, day] = dateToArray(rangeMode.value ? startStr : endStr)
  pickedDate.value = [year, month, day]
  viewYear.value = year
  viewMonth.value = month
  monthDates.value = getDatesByYM(year, month)
  syncPickedDateToViewMonth()
}

/** セルの日付（当月セルのみ有効） */
function cellDate(cell) {
  if (!cell?.currentMonth) return null
  return dayjs(`${viewYear.value}-${viewMonth.value}-${cell.date}`)
}

function isSelected(cell) {
  if (rangeMode.value) return false
  const [y, m, d] = pickedDate.value
  return (
    cell.currentMonth &&
    viewYear.value === y &&
    viewMonth.value === m &&
    cell.date === d
  )
}

/** 期間モードの両端（開始日・終了日）セル */
function isRangeEdge(cell) {
  if (!rangeMode.value) return false
  const d = cellDate(cell)
  if (!d) return false
  const key = d.format('YYYY-MM-DD')
  return key === rangeStart.value || key === rangeEnd.value
}

/** 期間モードで開始日と終了日に挟まれたセル（不正な期間のときは塗らない） */
function isInRange(cell) {
  if (!rangeMode.value || !rangeValid.value) return false
  const d = cellDate(cell)
  if (!d) return false
  return d.isAfter(dayjs(rangeStart.value)) && d.isBefore(dayjs(rangeEnd.value))
}

/**
 * モード切替。期間 → 単日では終了日をその日の単日選択として引き継ぐ。
 * 単日 → 期間では現在の日を開始日・終了日の両方に入れ、まず終了日の入力待ちにする。
 */
function setRangeMode(next) {
  if (rangeMode.value === next) return
  rangeMode.value = next
  if (next) {
    const [y, m, d] = pickedDate.value
    const current = dayjs(`${y}-${m}-${d}`).format('YYYY-MM-DD')
    rangeStart.value = current
    rangeEnd.value = current
    activeField.value = 'end'
  } else {
    const [y, m, d] = dateToArray(dayjs(rangeEnd.value).format('YYYY-M-D'))
    pickedDate.value = [y, m, d]
  }
}

function isTodayCell(cell) {
  if (!cell?.currentMonth) return false
  const now = new Date()
  return (
    viewYear.value === now.getFullYear()
    && viewMonth.value === now.getMonth() + 1
    && cell.date === now.getDate()
  )
}

async function toggleYearDropMenu() {
  showYearDropMenu.value = !showYearDropMenu.value
  if (!showYearDropMenu.value) return

  await nextTick()
  const dropdown = yearDropdown.value
  const selectedYear = dropdown?.querySelector('.y-menu.select')
  if (!dropdown || !selectedYear) return

  dropdown.scrollTop = selectedYear.offsetTop
    - (dropdown.clientHeight - selectedYear.offsetHeight) / 2
}

function onYearClick(year) {
  viewYear.value = year
  monthDates.value = getDatesByYM(year, viewMonth.value)
  showYearDropMenu.value = false
}

function onMonthClick(idx) {
  viewMonth.value = idx + 1
  monthDates.value = getDatesByYM(viewYear.value, viewMonth.value)
}

function onDateClick(idx) {
  const cell = monthDates.value[idx]
  if (!cell?.currentMonth) return

  const picked = dayjs(`${viewYear.value}-${viewMonth.value}-${cell.date}`).format('YYYY-MM-DD')

  if (!rangeMode.value) {
    // 単日モードは従来どおりクリックで即確定
    pickedDate.value = [viewYear.value, viewMonth.value, cell.date]
    emit('back', { start: picked, end: picked })
    return
  }

  // 期間モード：アクティブな側だけを更新する。
  // 開始日を入れたら自動で終了日待ちに移り、終了日を入れたらそこで止める（不正なら下にエラーを出す）。
  if (activeField.value === 'start') {
    rangeStart.value = picked
    activeField.value = 'end'
  } else {
    rangeEnd.value = picked
  }
}

/** 期間の確定。妥当でないときはボタン自体が無効なので、ここには来ない（多重防御で再チェック） */
function applyRange() {
  if (!rangeValid.value) return
  emit('back', { start: rangeStart.value, end: rangeEnd.value })
}

function handleBack() {
  emit('back')
}

watch(
  () => [props.selectStartDate, props.selectEndDate, props.selectMinDate, props.selectMaxDate],
  () => initCalendar(),
  { immediate: true },
)
</script>

<style lang="less" scoped>
.center {
  display: flex;
  align-items: center;
  justify-content: center;
}

.calendar-pc {
  --cal-bg: var(--bg-body, #fff);
  --cal-panel: var(--bg-panel, #fff);
  --cal-panel-soft: color-mix(in srgb, var(--brand-color, #f9da49) 8%, var(--bg-panel, #fff));
  --cal-chip: color-mix(in srgb, var(--brand-color, #f9da49) 18%, var(--bg-panel, #fff));
  --cal-hover: color-mix(in srgb, var(--brand-color, #f9da49) 12%, var(--bg-panel, #fff));
  --cal-today-bg: color-mix(in srgb, var(--brand-color, #f9da49) 18%, var(--bg-panel, #fff));
  --cal-today-border: color-mix(in srgb, var(--brand-color, #f9da49) 74%, #d2b112);
  --cal-today-label-bg: color-mix(in srgb, var(--brand-color, #f9da49) 26%, var(--bg-panel, #fff));
  --cal-today-label-text: color-mix(in srgb, var(--text-primary, #000) 85%, #8a6f00);
  --cal-today-num: color-mix(in srgb, var(--text-primary, #000) 88%, #8a6f00);
  --cal-select-label-bg: color-mix(in srgb, #fff 65%, transparent);
  --cal-border: var(--border-color, #e5e5e5);
  --cal-text: var(--text-primary, #000);
  --cal-text-muted: var(--text-secondary, #4a4a4a);
  --cal-accent: var(--brand-color, #f9da49);
  --cal-scrollbar-thumb: color-mix(in srgb, var(--brand-color, #f9da49) 42%, #a8adb5);
  --cal-scrollbar-thumb-hover: color-mix(in srgb, var(--brand-color, #f9da49) 62%, #858b93);
  /* 品牌黄底上始终用深色字，不受 dark 文本色影响 */
  --cal-accent-text: #181818;
  --cal-festival: var(--accent-orange, #f97316);

  height: 100%;
  background: var(--cal-bg);
  color: var(--cal-text);
  box-shadow: var(--shadow-md);
}

/* 暗色模式：用 panel 叠色替代偏亮色混入，保证对比可读 */
html.dark .calendar-pc {
  --cal-panel-soft: color-mix(in srgb, var(--brand-color, #f9da49) 6%, var(--bg-panel, #20242b));
  --cal-chip: color-mix(in srgb, var(--brand-color, #f9da49) 14%, var(--bg-panel, #20242b));
  --cal-hover: color-mix(in srgb, var(--brand-color, #f9da49) 10%, var(--bg-panel, #20242b));
  --cal-today-bg: color-mix(in srgb, var(--brand-color, #f9da49) 14%, var(--bg-panel, #20242b));
  --cal-today-border: color-mix(in srgb, var(--brand-color, #f9da49) 55%, #8a7320);
  --cal-today-label-bg: color-mix(in srgb, var(--brand-color, #f9da49) 22%, var(--bg-panel, #20242b));
  --cal-today-label-text: color-mix(in srgb, #f0f0f0 70%, var(--brand-color, #cc251b));
  --cal-today-num: color-mix(in srgb, #f0f0f0 72%, var(--brand-color, #cc251b));
  --cal-select-label-bg: color-mix(in srgb, #181818 28%, transparent);
  --cal-accent-text: #181818;
  --cal-festival: var(--accent-orange, #fb923c);
  --cal-scrollbar-thumb: color-mix(in srgb, var(--brand-color, #f9da49) 30%, #666d76);
  --cal-scrollbar-thumb-hover: color-mix(in srgb, var(--brand-color, #f9da49) 52%, #858c95);
}

.title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  padding: 0 24px;
  background: color-mix(in srgb, var(--brand-color, #f9da49) 12%, var(--cal-panel));
  border-bottom: 1px solid var(--cal-border);
  font-size: 16px;

  .left {
    gap: 10px;
  }

  .title-icon {
    color: var(--cal-accent);
    font-size: 14px;
  }

  .right {
    gap: 8px;
    border: none;
    background: transparent;
    color: var(--cal-text);
    cursor: pointer;
    font-size: 16px;

    &:hover {
      color: var(--cal-accent);
    }
  }

  .back-icon {
    font-size: 12px;
    color: var(--cal-text-muted);
  }
}

.content {
  padding: 0 32px 24px;
}

/* 単日／期間の切替と、期間モードの開始日・終了日入力 */
.mode-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
}

.mode-switch {
  display: inline-flex;
  border: 1px solid var(--cal-border);
  border-radius: 6px;
  overflow: hidden;
}

.mode-switch__btn {
  min-width: 72px;
  height: 34px;
  padding: 0 14px;
  border: none;
  border-left: 1px solid var(--cal-border);
  background: var(--cal-panel);
  color: var(--cal-text-muted);
  font-size: 14px;
  cursor: pointer;

  &:first-child {
    border-left: 0;
  }

  &:hover {
    background: var(--cal-hover);
  }

  &.active {
    background: var(--cal-accent);
    color: var(--cal-accent-text);
    font-weight: 600;
  }
}

.range-fields {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.range-field {
  display: inline-flex;
  /* ラベルと日付でフォントサイズが違うため baseline 揃えだと上寄りに見える。
     行ボックスの高さも 1 に固定して、枠の中央にそろえる */
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 14px;
  line-height: 1;
  border: 1px solid var(--cal-border);
  border-radius: 6px;
  background: var(--cal-panel);
  color: var(--cal-text);
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: var(--cal-hover);
  }

  /* 入力待ちの側を強調：次のクリックがどちらに入るかを見せる */
  &.active {
    border-color: var(--cal-accent);
    background: var(--cal-chip);
    font-weight: 600;
  }
}

.range-field__label {
  color: var(--cal-text-muted);
  font-size: 12px;
  line-height: 1;
}

.range-field__value {
  line-height: 1;
  /* 未選択→選択、通常→太字で幅が跳ねないよう最低幅を確保する */
  min-width: 78px;
  text-align: left;
  font-variant-numeric: tabular-nums;
}

.range-sep {
  color: var(--cal-text-muted);
}

.range-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

.range-hint {
  color: var(--cal-text-muted);
  font-size: 12px;
}

.range-apply {
  height: 34px;
  padding: 0 20px;
  border: 1px solid var(--cal-accent);
  border-radius: 6px;
  background: var(--cal-accent);
  color: var(--cal-accent-text);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    border-color: var(--cal-border);
    background: var(--cal-panel);
    color: var(--cal-text-muted);
    cursor: not-allowed;
    opacity: 0.7;
  }
}

.range-error {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 10px 0 0;
  color: var(--el-color-danger, #cc251b);
  font-size: 13px;
}

.content .header {
  display: flex;
  align-items: center;
  margin-top: 16px;
}

.years {
  position: relative;
  z-index: 10;
  margin-left: -2px;
}

.y-current {
  width: 110px;
  height: 40px;
  gap: 6px;
  border: 1px solid var(--cal-border);
  border-radius: 4px;
  background: var(--cal-chip);
  color: var(--cal-text);
  font-size: 18px;
  cursor: pointer;
}

.caret {
  width: 0;
  height: 0;
  border-top: 5px solid var(--cal-text);
  border-right: 4px solid transparent;
  border-left: 4px solid transparent;
  transition: transform 0.3s linear;

  &.up {
    transform: rotate(180deg);
  }
}

.y-dropdown {
  position: absolute;
  top: 44px;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 110px;
  max-height: min(360px, calc(100dvh - 180px));
  padding: 4px;
  overflow-x: hidden;
  overflow-y: auto;
  border: 1px solid var(--cal-border);
  border-radius: 8px;
  background: var(--cal-panel);
  box-shadow: var(--shadow-md);
  scrollbar-color: var(--cal-scrollbar-thumb) transparent;
  scrollbar-width: thin;
  overscroll-behavior: contain;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 5px 0;
  }

  &::-webkit-scrollbar-thumb {
    min-height: 36px;
    background-color: var(--cal-scrollbar-thumb);
    background-clip: padding-box;
    border: 2px solid transparent;
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: var(--cal-scrollbar-thumb-hover);
  }
}

.y-menu {
  flex-shrink: 0;
  width: 96px;
  height: 32px;
  margin: 4px 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--cal-text);
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: var(--cal-hover);
  }

  &.select {
    color: var(--cal-accent-text);
    background: var(--cal-accent);
    font-weight: 600;
  }
}

.months {
  display: flex;
  flex: 1;
  height: 40px;
  margin-left: 24px;
  border: 1px solid var(--cal-border);
  border-radius: 4px;
  color: var(--cal-text-muted);
  font-size: 14px;
  overflow: hidden;
  background: var(--cal-panel);
}

.month {
  flex: 1;
  border: none;
  border-left: 1px solid var(--cal-border);
  background: transparent;
  color: inherit;
  cursor: pointer;

  &:first-child {
    border-left: 0;
  }

  &:hover {
    background: var(--cal-hover);
  }

  &.select {
    border-color: transparent;
    border-radius: 4px;
    background: var(--cal-accent);
    color: var(--cal-accent-text);
    font-weight: 600;
    transform: scale(1.03);
  }
}

.body {
  margin-top: 20px;
  border: 1px solid var(--cal-border);
  border-radius: 4px;
  background: var(--cal-panel-soft);
}

.weeks {
  display: flex;
  height: 52px;
  border-radius: 4px 4px 0 0;
  color: var(--cal-text);
  font-size: 13px;
  opacity: 0.7;
}

.week {
  flex: 1;
}

.dates {
  display: flex;
  flex-wrap: wrap;
}

.date {
  position: relative;
  width: 14.2857%;
  height: 88px;
  border: none;
  border-top: 1px solid var(--cal-border);
  border-left: 1px solid var(--cal-border);
  background: transparent;
  cursor: pointer;

  &:nth-child(7n + 1) {
    border-left: 0;
  }

  &:not(.disable):hover::before,
  &.select::before,
  &.range-edge::before,
  &.in-range::before {
    content: '';
    position: absolute;
    inset: 4px;
    border-radius: 4px;
    background: var(--cal-hover);
  }

  &.select::before,
  &.range-edge::before {
    background: var(--cal-accent);
  }

  /* 期間の中間日：両端より薄く塗って「帯」に見せる */
  &.in-range::before {
    background: var(--cal-chip);
  }

  &.today::before {
    content: '';
    position: absolute;
    inset: 6px;
    border: 1px solid var(--cal-today-border);
    border-radius: 6px;
    background: var(--cal-today-bg);
  }

  &.today.select::before,
  &.today.range-edge::before {
    inset: 4px;
    border: none;
    border-radius: 4px;
    background: var(--cal-accent);
  }

  &.disable {
    cursor: not-allowed;

    &:hover::before {
      display: none;
    }

    .num {
      opacity: 0.3;
    }
  }

  &.select .num,
  &.select .festival,
  &.range-edge .num,
  &.range-edge .festival {
    color: var(--cal-accent-text);
  }

  &.select .today-label,
  &.range-edge .today-label {
    color: var(--cal-accent-text);
    background: var(--cal-select-label-bg);
  }

  &.today .num {
    color: var(--cal-today-num);
    font-weight: 700;
  }

  &.today.select .num,
  &.today.range-edge .num {
    color: var(--cal-accent-text);
  }
}

.num {
  position: absolute;
  top: 12px;
  right: 18px;
  height: 28px;
  color: var(--cal-text);
  font-size: 20px;
  line-height: 28px;
  z-index: 1;
}

.today-label {
  position: absolute;
  top: 14px;
  left: 10px;
  z-index: 1;
  padding: 1px 6px;
  border-radius: 10px;
  background: var(--cal-today-label-bg);
  color: var(--cal-today-label-text);
  font-size: 10px;
  font-weight: 700;
  line-height: 1.3;
}

.festival {
  position: absolute;
  right: 6px;
  bottom: 8px;
  left: 6px;
  z-index: 1;
  color: var(--cal-festival);
  font-size: 11px;
  line-height: 1.2;
  text-align: right;
  white-space: normal;
  word-break: keep-all;

  &--long {
    font-size: 9px;
  }
}

@media (max-width: 768px) {
  .title {
    /* iPhone 刘海/状态栏：title 栏避开顶部安全区，返回按钮不被遮挡 */
    height: calc(44px + env(safe-area-inset-top, 0px));
    padding: 0 12px;
    padding-top: env(safe-area-inset-top, 0px);
    font-size: 15px;
  }

  .content {
    padding: 0 10px 14px;
  }

  .content .header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  /* スマホ：モード切替→期間入力→適用の順に縦積みし、指で押せる高さを確保する */
  .mode-bar {
    margin-top: 14px;
    gap: 10px;
  }

  .mode-switch,
  .range-fields {
    width: 100%;
  }

  .mode-switch__btn {
    flex: 1;
    height: 38px;
  }

  .range-field {
    flex: 1;
    min-width: 0;
    height: 38px;
    padding: 0 10px;
    justify-content: center;
  }

  .range-field__value {
    min-width: 0;
    text-align: center;
  }

  .range-actions {
    width: 100%;
    margin-left: 0;
    justify-content: space-between;
  }

  .range-apply {
    flex: 1;
    height: 38px;
  }

  .months {
    margin-left: 0;
    flex-wrap: wrap;
    height: auto;
    overflow: visible;
  }

  .month {
    flex: 0 0 25%;
    height: 40px;
    min-height: 40px;
    border-left: 0;
    border-top: 1px solid var(--cal-border);

    &.select {
      transform: none;
      z-index: 1;
    }
  }

  .weeks {
    height: 40px;
    font-size: 12px;
  }

  .date {
    height: 56px;
    min-height: 56px;

    &:not(.disable):hover::before,
    &.select::before {
      inset: 2px;
    }

    &.today::before {
      inset: 3px;
    }
  }

  .num {
    top: 8px;
    right: 8px;
    height: 22px;
    font-size: 15px;
    line-height: 22px;
  }

  .today-label {
    top: 8px;
    left: 4px;
    padding: 1px 4px;
    font-size: 8px;
  }

  .festival {
    right: 3px;
    bottom: 3px;
    left: 3px;
    font-size: 7px;
    line-height: 1.1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

@media (max-width: 400px) {
  .festival {
    right: 2px;
    bottom: 3px;
    left: 2px;
    display: block;
    font-size: 6px;
    line-height: 1.15;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .date {
    height: 52px;
    min-height: 52px;
  }

  .num {
    top: 6px;
    right: 6px;
    font-size: 14px;
  }

  .today-label {
    top: 6px;
    left: 3px;
  }
}
</style>
